import type { FunnelCta } from "./tracking";

/**
 * Every TM+ CTA in Lume, in one place.
 *
 * ── THE SCHEME ───────────────────────────────────────────────────────────────
 * `location` is the TOOL inside Lume; `element` is the PRESENTATION of the CTA;
 * `type` is what the student ran into. That split is the whole point: it lets a
 * report cut by tool ("does the corrector convert better than Praticar?") and by
 * shape ("do strips convert at all?") without either question needing an element
 * name to be parsed, and it lets the same piece appear on two screens under one
 * element with the location telling them apart — which is exactly what
 * `gate_screen` needs.
 *
 * The site's own funnel uses the same axis with no prefix (`corrector`,
 * `tutor_ia`, `exercises`), so the `lume_` prefix is what separates Lume's
 * corrector from the site's while keeping the two comparable.
 *
 * ── THE RENAME, 2026Q4 ───────────────────────────────────────────────────────
 * These names REPLACE seventeen that had GA4 history, and that break is
 * deliberate and signed off — not an accident to be quietly undone. The old
 * names carried the placement in the element (`pratica_limit_dashboard`,
 * `corrector_theme_gate`) and left `location` as a near-constant `lume` /
 * `lume_corrector`, which made the tool cut impossible. Every funnel report
 * reading the old elements restarts at the cutover; the property is annotated
 * with the date.
 *
 *   trilha_gate                  → lume_trilha|lume_simulados / gate_screen
 *   corrector_competence_lock    → lume_redacao   / partial_card
 *   corrector_competence_inline  → lume_redacao   / panel_strip
 *   corrector_limit_modal        → lume_redacao   / limit_modal
 *   corrector_limit_writer       → lume_redacao   / editor_strip
 *   corrector_theme_gate         → lume_redacao   / gate_band + theme_card
 *   corrector_limit_chooser      → lume_redacao   / chooser_card
 *   simulado_limit               → lume_simulados / builder_card
 *   simulado_limit_modal         → lume_simulados / limit_modal
 *   pratica_limit_escolher       → lume_praticar  / band_top
 *   pratica_run_limit            → lume_praticar  / session_end_card
 *   pratica_limit_dashboard      → lume_praticar  / notice_strip
 *   trilha_ribbon                → lume_trilha    / ribbon_top
 *   materias_ribbon              → lume_materias  / ribbon_top
 *   chat_daily_limit             → lume_tutor     / chat_turn
 *   sidebar_caderno   ┐
 *   sidebar_hub       ┘          → lume_chrome    / sidebar_upsell
 *
 * TWO ELEMENTS MERGED, one dimension lost: the sidebar row no longer says
 * whether it was the hub's or a caderno's. It is the same row with the same
 * words in the same place either way, and `lume_chrome` is one surface by
 * design. If that split turns out to matter, it comes back as a location
 * (`lume_chrome_hub`), not as two elements.
 *
 * ALSO NOTE: `tm_last_cta` in localStorage has a 30-day TTL, so the server-side
 * purchase event will report a mix of old and new names for a month after the
 * cutover. Nothing to fix — just do not read a clean split before then.
 */

/**
 * The tools. `lume_chrome` is the app's own furniture — the sidebar — and not a
 * tool at all; it is here because a CTA that lives on every screen belongs to no
 * single one of them.
 */
export const LUME_LOCATIONS = {
  chrome: "lume_chrome",
  materias: "lume_materias",
  praticar: "lume_praticar",
  redacao: "lume_redacao",
  simulados: "lume_simulados",
  trilha: "lume_trilha",
  tutor: "lume_tutor",
  tutorEmpty: "lume_tutor_empty",
} as const;

/* ── Redação ─────────────────────────────────────────────────────────────────
 * Seven placements, and the reason they are seven rather than one is intent: a
 * student browsing temas with nothing left to spend, one who has written the
 * essay, and one who pressed "Corrigir" anyway are three different populations.
 * Averaging them hides that the third is the one worth optimising. */

/** The card above a partial correction, with the grade already delivered. */
export const REDACAO_PARTIAL_CARD_CTA: FunnelCta = {
  element: "partial_card",
  location: LUME_LOCATIONS.redacao,
  type: "gate",
};

/** The first blurred competência, met while reading one's own desvios. */
export const REDACAO_PANEL_STRIP_CTA: FunnelCta = {
  element: "panel_strip",
  location: LUME_LOCATIONS.redacao,
  type: "gate",
};

/** The wall on the press of "Corrigir redação" — the highest intent in Lume. */
export const REDACAO_LIMIT_MODAL_CTA: FunnelCta = {
  element: "limit_modal",
  location: LUME_LOCATIONS.redacao,
  type: "limit",
};

/** The wall once a free student's essay photos are spent. */
export const REDACAO_PHOTO_LIMIT_MODAL_CTA: FunnelCta = {
  element: "photo_limit_modal",
  location: LUME_LOCATIONS.redacao,
  type: "limit",
};

/** The quota at the foot of the editor, with the essay already written. */
export const REDACAO_EDITOR_STRIP_CTA: FunnelCta = {
  element: "editor_strip",
  location: LUME_LOCATIONS.redacao,
  type: "limit",
};

/**
 * The full-page dead end behind `?tema=`, and ONLY that.
 *
 * A student reaches it one way: by following a link to a PLUS tema — from a
 * share, a bookmark, a campaign — and being stopped on arrival. That is a
 * population of one intent, which is the whole reason it has an element.
 *
 * It used to also carry the locked cards in the chooser's grid, and the two
 * together made the element unreadable: a card in a grid of temas is browsing,
 * a link that lands on a wall is intent, and the second is rare enough that the
 * first buried it. The cards are `theme_card` now — the split is deliberate,
 * dated with the rest of the 2026Q4 rename, and gate_band's history from before
 * it is a MIX of the two.
 */
export const REDACAO_GATE_BAND_CTA: FunnelCta = {
  element: "gate_band",
  location: LUME_LOCATIONS.redacao,
  type: "gate",
};

/**
 * One of the "Temas exclusivos" cards, in the chooser's second grid.
 *
 * `gate` and not `limit`: nothing ran out — these temas were never the free
 * student's to spend a correction on.
 *
 * ITS VIEW IS THE SECTION'S, NOT THE CARD'S, and it has to be. Six cards mount
 * together, so six view events would fire for one impression; the tracker dedupes
 * by (location, element, type) and would report the first, but that is the dedupe
 * covering for the call site rather than the call site being right. The section
 * announces itself once, when it renders with at least one locked tema in it —
 * see Redacao's Chooser. The clicks stay per-card, which is what a CTR over that
 * one impression wants.
 */
export const REDACAO_THEME_CARD_CTA: FunnelCta = {
  element: "theme_card",
  location: LUME_LOCATIONS.redacao,
  type: "gate",
};

/** The quota, seen while browsing temas with nothing left to spend. */
export const REDACAO_CHOOSER_CARD_CTA: FunnelCta = {
  element: "chooser_card",
  location: LUME_LOCATIONS.redacao,
  type: "limit",
};

/* ── Simulados ───────────────────────────────────────────────────────────── */

/** Top of "Montar um simulado", before the options. */
export const SIMULADO_BUILDER_CARD_CTA: FunnelCta = {
  element: "builder_card",
  location: LUME_LOCATIONS.simulados,
  type: "limit",
};

/** The free-plan strip under the Simulados header. */
export const SIMULADOS_PLAN_STRIP_CTA: FunnelCta = {
  element: "plan_strip",
  location: LUME_LOCATIONS.simulados,
  type: "limit",
};

/**
 * The wall after a whole simulado was built and the button pressed.
 *
 * Shares the `limit_modal` element with the corrector's — under this scheme that
 * is not a collision but the scheme working: `location` says which tool, and
 * "the modal that refuses on the press" is one shape worth reading across both.
 */
export const SIMULADO_LIMIT_MODAL_CTA: FunnelCta = {
  element: "limit_modal",
  location: LUME_LOCATIONS.simulados,
  type: "limit",
};

/* ── Praticar ────────────────────────────────────────────────────────────── */

/** Top of the chooser, where the offer is the subject of the screen. */
export const PRATICA_BAND_TOP_CTA: FunnelCta = {
  element: "band_top",
  location: LUME_LOCATIONS.praticar,
  type: "limit",
};

/** The closing card of a session that just hit the day's tenth question. */
export const PRATICA_SESSION_END_CTA: FunnelCta = {
  element: "session_end_card",
  location: LUME_LOCATIONS.praticar,
  type: "limit",
};

/** The free-questions card in the Praticar header. */
export const PRATICA_QUOTA_CARD_CTA: FunnelCta = {
  element: "quota_card",
  location: LUME_LOCATIONS.praticar,
  type: "limit",
};

/** The free-questions box on the end-of-round screen of a session. */
export const PRATICA_ROUND_END_CTA: FunnelCta = {
  element: "round_end_card",
  location: LUME_LOCATIONS.praticar,
  type: "limit",
};

/**
 * The note on the Praticar dashboard. ONE element for both of its states — the
 * week's simulado spent, and everything spent — because it is one strip in one
 * place; the copy differs, the placement does not.
 */
export const PRATICA_NOTICE_STRIP_CTA: FunnelCta = {
  element: "notice_strip",
  location: LUME_LOCATIONS.praticar,
  type: "limit",
};

/* ── Trilha, matérias, tutor, chrome ─────────────────────────────────────── */

/**
 * The standing ribbon at the top of a caderno screen.
 *
 * `gate` and no longer `layout`. It used to be `layout` on the argument that it
 * refuses nothing and is permanent chrome like the sidebar's row — true of its
 * BEHAVIOUR, but it made the reports read wrong: the ribbon only ever appears
 * because part of the page is outside the plan, so grouping it with header_cta
 * and footer_cta put a gate's impressions in the furniture's bucket. `layout`
 * now means what the sidebar row is: present regardless of what is locked.
 *
 * Two locations rather than two elements — it is the same 42px line, and the
 * screen it sits on is what differs.
 */
export const TRILHA_RIBBON_CTA: FunnelCta = {
  element: "ribbon_top",
  location: LUME_LOCATIONS.trilha,
  type: "gate",
};

/** The revisão final's band between the last free passo and the first locked one. */
export const TRILHA_PASSO_BAND_CTA: FunnelCta = {
  element: "passo_band",
  location: LUME_LOCATIONS.trilha,
  type: "gate",
};

/** A locked passo of the revisão final, which opens the same offer as the band. */
export const TRILHA_PASSO_LOCKED_CTA: FunnelCta = {
  element: "passo_locked",
  location: LUME_LOCATIONS.trilha,
  type: "gate",
};

/** A locked row or simulado card inside a trilha card, outside the revisão final's locked passos. */
export const TRILHA_STEP_LOCKED_CTA: FunnelCta = {
  element: "step_locked",
  location: LUME_LOCATIONS.trilha,
  type: "gate",
};

export const MATERIAS_RIBBON_CTA: FunnelCta = {
  element: "ribbon_top",
  location: LUME_LOCATIONS.materias,
  type: "gate",
};

/** The day's messages, spent, answered in the thread itself. */
export const TUTOR_CHAT_TURN_CTA: FunnelCta = {
  element: "chat_turn",
  location: LUME_LOCATIONS.tutor,
  type: "limit",
};

/** The agent's offer of a locked tópico's manual, drawn in the thread (show_unlock). */
export const CHAT_UNLOCK_CTA: FunnelCta = {
  element: "chat_unlock_card",
  location: LUME_LOCATIONS.tutor,
  type: "gate",
};

/** The agent's answer to a simulado asked for in a week already spent (show_unlock). */
export const CHAT_SIMULADO_UNLOCK_CTA: FunnelCta = {
  element: "chat_simulado_unlock",
  location: LUME_LOCATIONS.tutor,
  type: "limit",
};

/**
 * The sidebar's foot, on every screen. `layout`: nothing ran out and nothing is
 * refused — it is the one CTA a student sees all day.
 */
export const SIDEBAR_UPSELL_CTA: FunnelCta = {
  element: "sidebar_upsell",
  location: LUME_LOCATIONS.chrome,
  type: "layout",
};

/* ── The gate screen ────────────────────────────────────────────────────── */

/** Which tool's screen the full-page gate replaced. */
export type GateScreenWhere = "simulados" | "trilha";

/**
 * The full-page wall that stands in for a tópico, exercício, exame or simulado
 * outside the plan.
 *
 * A FUNCTION AND NOT A CONSTANT, because this is the one element that reports
 * under two locations: the same screen answers a locked tópico in the trilha and
 * a locked simulado, and those are different tools with different intent. Under
 * the old names it was one `trilha_gate` for both, which is why "do students who
 * hit the wall on a simulado convert differently?" could not be asked.
 *
 * The result is memoised per location, so a caller can hand it straight to
 * usePlusCta without re-firing the view effect on every render.
 */
const GATE_SCREEN_CTAS: Record<GateScreenWhere, FunnelCta> = {
  simulados: {
    element: "gate_screen",
    location: LUME_LOCATIONS.simulados,
    type: "gate",
  },
  trilha: {
    element: "gate_screen",
    location: LUME_LOCATIONS.trilha,
    type: "gate",
  },
};

export function gateScreenCta(where: GateScreenWhere = "trilha"): FunnelCta {
  return GATE_SCREEN_CTAS[where];
}

/* ── A modal de conversão ───────────────────────────────────────────────── */

/**
 * The modal every banner now opens on its way to the checkout.
 *
 * IT HAS NO LOCATION OF ITS OWN. It is called from seven tools, and what makes
 * its numbers worth reading is precisely which one sent the student: the same
 * modal converts differently after a spent redação than after a locked tópico.
 * So it borrows the origin's location and type wholesale and contributes only
 * the element, which is the rule the rest of this file follows — element is the
 * shape, location is the tool.
 *
 * Memoised per origin so it can be handed straight to an effect dependency.
 */
export const CONVERSION_MODAL_ELEMENT = "conversion_modal";

const CONVERSION_MODAL_CTAS = new Map<string, FunnelCta>();

export function conversionModalCta(origin: FunnelCta): FunnelCta {
  const key = `${origin.location}|${origin.type}`;
  const found = CONVERSION_MODAL_CTAS.get(key);

  if (found) return found;

  const cta: FunnelCta = {
    element: CONVERSION_MODAL_ELEMENT,
    location: origin.location,
    type: origin.type,
  };

  CONVERSION_MODAL_CTAS.set(key, cta);

  return cta;
}
